// Additional country content for SEO destination pages - Batch 2
import type { CountryContent } from "./countryContent";

export const additionalCountryContent: Record<string, CountryContent> = {
  "Afrique du Sud": {
    slug: "afrique-du-sud",
    flag: "🇿🇦",
    description: {
      fr: "L'Afrique du Sud éblouit par sa diversité : safaris dans le Kruger, vignobles du Cap, côte sauvage du Garden Route et rencontre avec les Big Five. La nation arc-en-ciel offre une expérience unique entre nature et culture.",
      en: "South Africa dazzles with its diversity: safaris in Kruger, Cape vineyards, wild Garden Route coast, and encounters with the Big Five. The rainbow nation offers a unique experience blending nature and culture.",
      es: "Sudáfrica deslumbra por su diversidad: safaris en Kruger, viñedos del Cabo, costa salvaje de Garden Route y encuentro con los Big Five. La nación arcoíris ofrece una experiencia única entre naturaleza y cultura."
    },
    extendedDescription: {
      fr: "Du Cap cosmopolite au parc Kruger, l'Afrique du Sud dévoile des paysages spectaculaires. La Table Mountain domine Cape Town, les vignobles de Stellenbosch ravissent les amateurs de vin, et les townships racontent l'histoire de Mandela.",
      en: "From cosmopolitan Cape Town to Kruger Park, South Africa unveils spectacular landscapes. Table Mountain dominates Cape Town, Stellenbosch vineyards delight wine lovers, and townships tell Mandela's story.",
      es: "Desde Ciudad del Cabo cosmopolita hasta el parque Kruger, Sudáfrica revela paisajes espectaculares."
    },
    keywords: {
      fr: ["voyage afrique du sud", "safari kruger", "cap", "garden route"],
      en: ["south africa trip", "kruger safari", "cape town", "garden route"],
      es: ["viaje sudáfrica", "safari kruger", "ciudad del cabo", "garden route"]
    },
    highlights: ["Le Cap", "Parc Kruger", "Garden Route", "Johannesburg", "Durban", "Stellenbosch"],
    bestTimeToVisit: {
      fr: "Mai à Septembre (saison sèche, idéale pour les safaris)",
      en: "May to September (dry season, ideal for safaris)",
      es: "Mayo a Septiembre (temporada seca, ideal para safaris)"
    },
    relatedCountries: ["Namibie", "Botswana", "Zimbabwe", "Mozambique"]
  },
  "Botswana": {
    slug: "botswana",
    flag: "🇧🇼",
    description: {
      fr: "Le Botswana est un joyau de l'Afrique australe : le delta de l'Okavango, les éléphants de Chobe, les étendues sauvages du Kalahari et les safaris parmi les plus exclusifs au monde.",
      en: "Botswana is a gem of Southern Africa: the Okavango Delta, Chobe elephants, wild Kalahari expanses, and some of the world's most exclusive safaris.",
      es: "Botsuana es una joya de África austral: el delta del Okavango, los elefantes de Chobe, las extensiones salvajes del Kalahari y safaris de los más exclusivos del mundo."
    },
    extendedDescription: {
      fr: "Le delta de l'Okavango, patrimoine UNESCO, offre des safaris en mokoro uniques. Chobe abrite la plus grande concentration d'éléphants d'Afrique. Le Botswana privilégie un tourisme durable et haut de gamme.",
      en: "The UNESCO-listed Okavango Delta offers unique mokoro safaris. Chobe hosts Africa's largest elephant concentration. Botswana prioritizes sustainable and high-end tourism.",
      es: "El delta del Okavango, patrimonio UNESCO, ofrece safaris en mokoro únicos. Chobe alberga la mayor concentración de elefantes de África."
    },
    keywords: {
      fr: ["voyage botswana", "okavango delta", "safari chobe", "kalahari"],
      en: ["botswana trip", "okavango delta", "chobe safari", "kalahari desert"],
      es: ["viaje botsuana", "delta okavango", "safari chobe", "kalahari"]
    },
    highlights: ["Delta de l'Okavango", "Parc de Chobe", "Désert du Kalahari", "Maun", "Moremi", "Makgadikgadi"],
    bestTimeToVisit: {
      fr: "Mai à Octobre (saison sèche)",
      en: "May to October (dry season)",
      es: "Mayo a Octubre (temporada seca)"
    },
    relatedCountries: ["Namibie", "Zimbabwe", "Zambie", "Afrique du Sud"]
  },
  "Zimbabwe": {
    slug: "zimbabwe",
    flag: "🇿🇼",
    description: {
      fr: "Le Zimbabwe fascine par les majestueuses chutes Victoria, les ruines du Grand Zimbabwe et ses parcs nationaux préservés. Une destination authentique pour découvrir l'Afrique australe hors des sentiers battus.",
      en: "Zimbabwe fascinates with the majestic Victoria Falls, Great Zimbabwe ruins, and pristine national parks. An authentic destination to discover Southern Africa off the beaten path.",
      es: "Zimbabue fascina con las majestuosas cataratas Victoria, las ruinas del Gran Zimbabue y sus parques nacionales preservados. Un destino auténtico para descubrir África austral fuera de los caminos trillados."
    },
    extendedDescription: {
      fr: "Les chutes Victoria, parmi les plus spectaculaires au monde, sont surnommées 'la fumée qui gronde'. Le parc national de Hwange abrite une faune exceptionnelle. Les ruines du Grand Zimbabwe témoignent d'une civilisation ancienne.",
      en: "Victoria Falls, among the world's most spectacular, are nicknamed 'the smoke that thunders'. Hwange National Park hosts exceptional wildlife. Great Zimbabwe ruins testify to an ancient civilization.",
      es: "Las cataratas Victoria, entre las más espectaculares del mundo, se llaman 'el humo que truena'. El parque Hwange alberga fauna excepcional."
    },
    keywords: {
      fr: ["voyage zimbabwe", "chutes victoria", "hwange safari", "grand zimbabwe"],
      en: ["zimbabwe trip", "victoria falls", "hwange safari", "great zimbabwe"],
      es: ["viaje zimbabue", "cataratas victoria", "safari hwange", "gran zimbabue"]
    },
    highlights: ["Chutes Victoria", "Parc de Hwange", "Grand Zimbabwe", "Matobo Hills", "Lac Kariba", "Mana Pools"],
    bestTimeToVisit: {
      fr: "Mai à Octobre (saison sèche)",
      en: "May to October (dry season)",
      es: "Mayo a Octubre (temporada seca)"
    },
    relatedCountries: ["Zambie", "Botswana", "Mozambique", "Afrique du Sud"]
  },
  "Zambie": {
    slug: "zambie",
    flag: "🇿🇲",
    description: {
      fr: "La Zambie offre une Afrique authentique : les chutes Victoria côté zambien, des safaris à pied pionniers, le parc South Luangwa et les communautés locales accueillantes.",
      en: "Zambia offers authentic Africa: Victoria Falls from the Zambian side, pioneering walking safaris, South Luangwa Park, and welcoming local communities.",
      es: "Zambia ofrece un África auténtica: las cataratas Victoria del lado zambiano, safaris a pie pioneros, el parque South Luangwa y comunidades locales acogedoras."
    },
    extendedDescription: {
      fr: "Berceau du safari à pied, la Zambie offre des expériences uniques. South Luangwa est réputé pour les léopards et les safaris nocturnes. Lower Zambezi permet des safaris en canoë sur le fleuve.",
      en: "Birthplace of walking safaris, Zambia offers unique experiences. South Luangwa is renowned for leopards and night safaris. Lower Zambezi allows canoe safaris on the river.",
      es: "Cuna del safari a pie, Zambia ofrece experiencias únicas. South Luangwa es famoso por los leopardos y safaris nocturnos."
    },
    keywords: {
      fr: ["voyage zambie", "chutes victoria zambie", "south luangwa", "safari zambie"],
      en: ["zambia trip", "victoria falls zambia", "south luangwa", "zambia safari"],
      es: ["viaje zambia", "cataratas victoria zambia", "south luangwa", "safari zambia"]
    },
    highlights: ["Chutes Victoria", "South Luangwa", "Lower Zambezi", "Livingstone", "Lac Tanganyika", "Kafue"],
    bestTimeToVisit: {
      fr: "Mai à Octobre (saison sèche)",
      en: "May to October (dry season)",
      es: "Mayo a Octubre (temporada seca)"
    },
    relatedCountries: ["Zimbabwe", "Botswana", "Malawi", "Tanzanie"]
  },
  "Madagascar": {
    slug: "madagascar",
    flag: "🇲🇬",
    description: {
      fr: "Madagascar est un monde à part : lémuriens endémiques, baobabs majestueux, forêts tropicales uniques et plages paradisiaques. L'île-continent offre une biodiversité exceptionnelle introuvable ailleurs sur Terre.",
      en: "Madagascar is a world apart: endemic lemurs, majestic baobabs, unique rainforests, and paradise beaches. The island-continent offers exceptional biodiversity found nowhere else on Earth.",
      es: "Madagascar es un mundo aparte: lémures endémicos, baobabs majestuosos, selvas tropicales únicas y playas paradisíacas. El continente-isla ofrece una biodiversidad excepcional que no existe en ningún otro lugar."
    },
    extendedDescription: {
      fr: "Des Tsingy de Bemaraha aux allées de baobabs, Madagascar révèle des paysages extraordinaires. L'île abrite 5% de la biodiversité mondiale dont 80% d'espèces endémiques. Nosy Be offre plages et plongée.",
      en: "From Bemaraha Tsingy to baobab avenues, Madagascar reveals extraordinary landscapes. The island hosts 5% of world biodiversity with 80% endemic species. Nosy Be offers beaches and diving.",
      es: "De los Tsingy de Bemaraha a las avenidas de baobabs, Madagascar revela paisajes extraordinarios."
    },
    keywords: {
      fr: ["voyage madagascar", "lémuriens", "allée des baobabs", "nosy be"],
      en: ["madagascar trip", "lemurs", "baobab avenue", "nosy be"],
      es: ["viaje madagascar", "lémures", "avenida de baobabs", "nosy be"]
    },
    highlights: ["Antananarivo", "Allée des Baobabs", "Nosy Be", "Tsingy de Bemaraha", "Parc de l'Isalo", "Île Sainte-Marie"],
    bestTimeToVisit: {
      fr: "Avril à Novembre (saison sèche)",
      en: "April to November (dry season)",
      es: "Abril a Noviembre (temporada seca)"
    },
    relatedCountries: ["Maurice", "Réunion", "Mozambique", "Tanzanie"]
  },
  "Cameroun": {
    slug: "cameroun",
    flag: "🇨🇲",
    description: {
      fr: "Le Cameroun est l'Afrique en miniature : forêts tropicales, savanes, montagnes volcaniques et plages. Des gorilles du sud aux traditions du nord, c'est un voyage dans toute la diversité africaine.",
      en: "Cameroon is Africa in miniature: tropical forests, savannas, volcanic mountains, and beaches. From southern gorillas to northern traditions, it's a journey through all of Africa's diversity.",
      es: "Camerún es África en miniatura: selvas tropicales, sabanas, montañas volcánicas y playas. De los gorilas del sur a las tradiciones del norte, es un viaje por toda la diversidad africana."
    },
    extendedDescription: {
      fr: "Du Mont Cameroun aux parcs de Waza et Lobéké, le pays offre une nature variée. Kribi séduit avec ses plages et chutes, tandis que les royaumes Bamoun et Bamiléké préservent des traditions ancestrales.",
      en: "From Mount Cameroon to Waza and Lobéké parks, the country offers varied nature. Kribi seduces with beaches and waterfalls, while Bamoun and Bamiléké kingdoms preserve ancestral traditions.",
      es: "Del Monte Camerún a los parques de Waza y Lobéké, el país ofrece naturaleza variada."
    },
    keywords: {
      fr: ["voyage cameroun", "mont cameroun", "kribi", "safari waza"],
      en: ["cameroon trip", "mount cameroon", "kribi beaches", "waza safari"],
      es: ["viaje camerún", "monte camerún", "kribi", "safari waza"]
    },
    highlights: ["Yaoundé", "Douala", "Kribi", "Mont Cameroun", "Parc de Waza", "Chutes de la Lobé"],
    bestTimeToVisit: {
      fr: "Novembre à Février (saison sèche nord) ou Décembre à Mars (sud)",
      en: "November to February (northern dry season) or December to March (south)",
      es: "Noviembre a Febrero (temporada seca norte) o Diciembre a Marzo (sur)"
    },
    relatedCountries: ["Nigeria", "Gabon", "Congo", "Centrafrique"]
  },
  "Sri Lanka": {
    slug: "sri-lanka",
    flag: "🇱🇰",
    description: {
      fr: "Le Sri Lanka enchante par ses temples bouddhistes, ses plantations de thé verdoyantes, ses éléphants sauvages et ses plages tropicales. La perle de l'océan Indien offre une richesse culturelle et naturelle exceptionnelle.",
      en: "Sri Lanka enchants with Buddhist temples, lush tea plantations, wild elephants, and tropical beaches. The pearl of the Indian Ocean offers exceptional cultural and natural richness.",
      es: "Sri Lanka encanta con sus templos budistas, plantaciones de té verdes, elefantes salvajes y playas tropicales. La perla del océano Índico ofrece una riqueza cultural y natural excepcional."
    },
    extendedDescription: {
      fr: "Du triangle culturel (Sigiriya, Polonnaruwa, Anuradhapura) aux collines de Nuwara Eliya, le Sri Lanka séduit. Yala offre les meilleurs safaris de léopards, tandis que Mirissa permet l'observation des baleines.",
      en: "From the cultural triangle (Sigiriya, Polonnaruwa, Anuradhapura) to Nuwara Eliya hills, Sri Lanka seduces. Yala offers the best leopard safaris, while Mirissa allows whale watching.",
      es: "Del triángulo cultural (Sigiriya, Polonnaruwa, Anuradhapura) a las colinas de Nuwara Eliya, Sri Lanka seduce."
    },
    keywords: {
      fr: ["voyage sri lanka", "sigiriya", "train sri lanka", "safari yala"],
      en: ["sri lanka trip", "sigiriya", "sri lanka train", "yala safari"],
      es: ["viaje sri lanka", "sigiriya", "tren sri lanka", "safari yala"]
    },
    highlights: ["Colombo", "Sigiriya", "Kandy", "Ella", "Galle", "Parc de Yala"],
    bestTimeToVisit: {
      fr: "Décembre à Mars (côte ouest) ou Avril à Septembre (côte est)",
      en: "December to March (west coast) or April to September (east coast)",
      es: "Diciembre a Marzo (costa oeste) o Abril a Septiembre (costa este)"
    },
    relatedCountries: ["Inde", "Maldives", "Thaïlande", "Népal"]
  },
  "Estonie": {
    slug: "estonie",
    flag: "🇪🇪",
    description: {
      fr: "L'Estonie surprend par sa modernité numérique et son charme médiéval. Tallinn, avec ses remparts préservés, côtoie une nature baltique préservée, des îles sauvages et une culture estonienne unique.",
      en: "Estonia surprises with its digital modernity and medieval charm. Tallinn, with its preserved ramparts, sits alongside pristine Baltic nature, wild islands, and unique Estonian culture.",
      es: "Estonia sorprende por su modernidad digital y su encanto medieval. Tallin, con sus murallas preservadas, convive con una naturaleza báltica virgen, islas salvajes y una cultura estonia única."
    },
    extendedDescription: {
      fr: "Tallinn possède l'une des vieilles villes médiévales les mieux préservées d'Europe. Les îles de Saaremaa et Hiiumaa offrent une nature sauvage. L'Estonie est aussi le pays le plus numérisé au monde.",
      en: "Tallinn has one of Europe's best-preserved medieval old towns. Saaremaa and Hiiumaa islands offer wild nature. Estonia is also the world's most digitized country.",
      es: "Tallin tiene uno de los cascos antiguos medievales mejor preservados de Europa."
    },
    keywords: {
      fr: ["voyage estonie", "tallinn", "îles estoniennes", "pays baltes"],
      en: ["estonia trip", "tallinn", "estonian islands", "baltic states"],
      es: ["viaje estonia", "tallin", "islas estonias", "países bálticos"]
    },
    highlights: ["Tallinn", "Tartu", "Île de Saaremaa", "Parc de Lahemaa", "Pärnu", "Île de Hiiumaa"],
    bestTimeToVisit: {
      fr: "Mai à Septembre",
      en: "May to September",
      es: "Mayo a Septiembre"
    },
    relatedCountries: ["Lettonie", "Lituanie", "Finlande", "Suède"]
  },
  "Lettonie": {
    slug: "lettonie",
    flag: "🇱🇻",
    description: {
      fr: "La Lettonie séduit par Riga, sa capitale art nouveau, ses châteaux romantiques et ses forêts de pins baltiques. Un pays authentique entre traditions et modernité au cœur des pays baltes.",
      en: "Latvia seduces with Riga, its Art Nouveau capital, romantic castles, and Baltic pine forests. An authentic country between traditions and modernity in the heart of the Baltic states.",
      es: "Letonia seduce con Riga, su capital art nouveau, castillos románticos y bosques de pinos bálticos. Un país auténtico entre tradiciones y modernidad en el corazón de los países bálticos."
    },
    extendedDescription: {
      fr: "Riga possède la plus grande concentration d'architecture art nouveau au monde. Le parc national de Gauja offre des paysages de vallées et châteaux. Jūrmala est la station balnéaire historique.",
      en: "Riga has the world's largest concentration of Art Nouveau architecture. Gauja National Park offers valley and castle landscapes. Jūrmala is the historic seaside resort.",
      es: "Riga tiene la mayor concentración de arquitectura art nouveau del mundo."
    },
    keywords: {
      fr: ["voyage lettonie", "riga", "art nouveau riga", "parc gauja"],
      en: ["latvia trip", "riga", "art nouveau riga", "gauja park"],
      es: ["viaje letonia", "riga", "art nouveau riga", "parque gauja"]
    },
    highlights: ["Riga", "Jūrmala", "Parc de Gauja", "Sigulda", "Liepāja", "Cēsis"],
    bestTimeToVisit: {
      fr: "Mai à Septembre",
      en: "May to September",
      es: "Mayo a Septiembre"
    },
    relatedCountries: ["Estonie", "Lituanie", "Biélorussie", "Suède"]
  },
  "Hongrie": {
    slug: "hongrie",
    flag: "🇭🇺",
    description: {
      fr: "La Hongrie enchante avec Budapest, la perle du Danube, ses bains thermaux légendaires, ses plaines de la Puszta et ses vins de Tokaj. Une destination d'Europe centrale riche en histoire et en saveurs.",
      en: "Hungary enchants with Budapest, the pearl of the Danube, legendary thermal baths, Puszta plains, and Tokaj wines. A Central European destination rich in history and flavors.",
      es: "Hungría encanta con Budapest, la perla del Danubio, sus baños termales legendarios, las llanuras de la Puszta y sus vinos de Tokaj. Un destino de Europa central rico en historia y sabores."
    },
    extendedDescription: {
      fr: "Budapest marie Buda historique et Pest dynamique. Les bains Széchenyi et Gellért sont emblématiques. La région du lac Balaton est la mer intérieure de Hongrie, tandis que Eger et Tokaj séduisent les amateurs de vin.",
      en: "Budapest marries historic Buda and dynamic Pest. Széchenyi and Gellért baths are iconic. The Balaton region is Hungary's inland sea, while Eger and Tokaj seduce wine lovers.",
      es: "Budapest une Buda histórico y Pest dinámico. Los baños Széchenyi y Gellért son emblemáticos."
    },
    keywords: {
      fr: ["voyage hongrie", "budapest", "bains thermaux hongrie", "tokaj"],
      en: ["hungary trip", "budapest", "thermal baths hungary", "tokaj wine"],
      es: ["viaje hungría", "budapest", "baños termales hungría", "tokaj"]
    },
    highlights: ["Budapest", "Lac Balaton", "Eger", "Tokaj", "Pécs", "Szentendre"],
    bestTimeToVisit: {
      fr: "Avril à Octobre",
      en: "April to October",
      es: "Abril a Octubre"
    },
    relatedCountries: ["Autriche", "Slovaquie", "Roumanie", "Croatie"]
  },
  "Nouvelle-Zélande": {
    slug: "nouvelle-zelande",
    flag: "🇳🇿",
    description: {
      fr: "La Nouvelle-Zélande émerveille par ses paysages épiques : fjords majestueux, volcans actifs, glaciers et collines de hobbits. Le pays des kiwis offre aventure et nature préservée au bout du monde.",
      en: "New Zealand amazes with epic landscapes: majestic fjords, active volcanoes, glaciers, and hobbit hills. The land of kiwis offers adventure and pristine nature at the end of the world.",
      es: "Nueva Zelanda maravilla con sus paisajes épicos: fiordos majestuosos, volcanes activos, glaciares y colinas de hobbits. El país de los kiwis ofrece aventura y naturaleza preservada en el fin del mundo."
    },
    extendedDescription: {
      fr: "De Milford Sound au parc Tongariro, la Nouvelle-Zélande est un paradis pour les amateurs de plein air. Queenstown est la capitale de l'aventure, Rotorua révèle la culture maorie, et Hobbiton fait revivre la Terre du Milieu.",
      en: "From Milford Sound to Tongariro Park, New Zealand is a paradise for outdoor enthusiasts. Queenstown is the adventure capital, Rotorua reveals Maori culture, and Hobbiton brings Middle-earth to life.",
      es: "De Milford Sound al parque Tongariro, Nueva Zelanda es un paraíso para los amantes del aire libre."
    },
    keywords: {
      fr: ["voyage nouvelle-zélande", "milford sound", "queenstown", "hobbiton"],
      en: ["new zealand trip", "milford sound", "queenstown", "hobbiton"],
      es: ["viaje nueva zelanda", "milford sound", "queenstown", "hobbiton"]
    },
    highlights: ["Auckland", "Queenstown", "Milford Sound", "Rotorua", "Wellington", "Parc Tongariro"],
    bestTimeToVisit: {
      fr: "Décembre à Février (été austral) ou Mai à Septembre (ski)",
      en: "December to February (austral summer) or May to September (skiing)",
      es: "Diciembre a Febrero (verano austral) o Mayo a Septiembre (esquí)"
    },
    relatedCountries: ["Australie", "Fidji", "Polynésie française", "Îles Cook"]
  },
  "Fidji": {
    slug: "fidji",
    flag: "🇫🇯",
    description: {
      fr: "Les Fidji sont un paradis tropical : 333 îles aux eaux turquoise, récifs coralliens spectaculaires, plages de sable blanc et hospitalité légendaire des Fidjiens. Le rêve polynésien par excellence.",
      en: "Fiji is a tropical paradise: 333 islands with turquoise waters, spectacular coral reefs, white sand beaches, and legendary Fijian hospitality. The quintessential Polynesian dream.",
      es: "Fiyi es un paraíso tropical: 333 islas con aguas turquesas, arrecifes de coral espectaculares, playas de arena blanca y legendaria hospitalidad fiyiana. El sueño polinesio por excelencia."
    },
    extendedDescription: {
      fr: "Des îles Mamanuca aux Yasawa, Fidji offre des escapades idylliques. La plongée est exceptionnelle, les cérémonies du kava authentiques. Viti Levu et Vanua Levu combinent aventure et détente.",
      en: "From the Mamanucas to the Yasawas, Fiji offers idyllic getaways. Diving is exceptional, kava ceremonies authentic. Viti Levu and Vanua Levu combine adventure and relaxation.",
      es: "De las islas Mamanuca a las Yasawa, Fiyi ofrece escapadas idílicas."
    },
    keywords: {
      fr: ["voyage fidji", "îles fidji", "plongée fidji", "mamanuca"],
      en: ["fiji trip", "fiji islands", "fiji diving", "mamanuca islands"],
      es: ["viaje fiyi", "islas fiyi", "buceo fiyi", "mamanuca"]
    },
    highlights: ["Nadi", "Îles Mamanuca", "Îles Yasawa", "Suva", "Taveuni", "Coral Coast"],
    bestTimeToVisit: {
      fr: "Mai à Octobre (saison sèche)",
      en: "May to October (dry season)",
      es: "Mayo a Octubre (temporada seca)"
    },
    relatedCountries: ["Nouvelle-Zélande", "Australie", "Vanuatu", "Tonga"]
  },
  "Éthiopie": {
    slug: "ethiopie",
    flag: "🇪🇹",
    description: {
      fr: "L'Éthiopie fascine par son histoire millénaire : églises rupestres de Lalibela, stèles d'Axoum, montagnes du Simien et traditions ancestrales. Le berceau de l'humanité offre un voyage dans le temps unique.",
      en: "Ethiopia fascinates with millennia of history: Lalibela rock churches, Axum stelae, Simien Mountains, and ancestral traditions. The cradle of humanity offers a unique journey through time.",
      es: "Etiopía fascina por su historia milenaria: iglesias rupestres de Lalibela, estelas de Axum, montañas Simien y tradiciones ancestrales. La cuna de la humanidad ofrece un viaje en el tiempo único."
    },
    extendedDescription: {
      fr: "Les églises monolithiques de Lalibela sont une merveille architecturale. Les montagnes du Simien abritent le loup d'Éthiopie et le babouin gelada. La vallée de l'Omo préserve des tribus aux traditions uniques.",
      en: "Lalibela's monolithic churches are an architectural wonder. Simien Mountains shelter the Ethiopian wolf and gelada baboon. The Omo Valley preserves tribes with unique traditions.",
      es: "Las iglesias monolíticas de Lalibela son una maravilla arquitectónica."
    },
    keywords: {
      fr: ["voyage éthiopie", "lalibela", "simien", "axoum"],
      en: ["ethiopia trip", "lalibela", "simien mountains", "axum"],
      es: ["viaje etiopía", "lalibela", "simien", "axum"]
    },
    highlights: ["Addis-Abeba", "Lalibela", "Gondar", "Axoum", "Montagnes du Simien", "Vallée de l'Omo"],
    bestTimeToVisit: {
      fr: "Octobre à Mars (saison sèche)",
      en: "October to March (dry season)",
      es: "Octubre a Marzo (temporada seca)"
    },
    relatedCountries: ["Kenya", "Tanzanie", "Érythrée", "Djibouti"]
  },
  "Côte d'Ivoire": {
    slug: "cote-d-ivoire",
    flag: "🇨🇮",
    description: {
      fr: "La Côte d'Ivoire séduit par sa diversité : Abidjan la vibrante, plages de Grand-Bassam, forêts tropicales du Mont Nimba et traditions akan. Le pays du cacao offre une Afrique de l'Ouest authentique.",
      en: "Ivory Coast seduces with its diversity: vibrant Abidjan, Grand-Bassam beaches, Mont Nimba tropical forests, and Akan traditions. The land of cocoa offers authentic West Africa.",
      es: "Costa de Marfil seduce por su diversidad: Abiyán vibrante, playas de Grand-Bassam, selvas tropicales del Monte Nimba y tradiciones akan. El país del cacao ofrece un África occidental auténtica."
    },
    extendedDescription: {
      fr: "Abidjan, la perle des lagunes, vibre entre modernité et traditions. Grand-Bassam, ancien comptoir colonial UNESCO, charme par son histoire. Le parc national de Taï abrite chimpanzés et forêt primaire.",
      en: "Abidjan, the pearl of lagoons, vibrates between modernity and traditions. Grand-Bassam, a former UNESCO colonial trading post, charms with its history. Taï National Park shelters chimpanzees and primary forest.",
      es: "Abiyán, la perla de las lagunas, vibra entre modernidad y tradiciones."
    },
    keywords: {
      fr: ["voyage côte d'ivoire", "abidjan", "grand-bassam", "parc taï"],
      en: ["ivory coast trip", "abidjan", "grand bassam", "tai park"],
      es: ["viaje costa de marfil", "abiyán", "grand bassam", "parque tai"]
    },
    highlights: ["Abidjan", "Grand-Bassam", "Yamoussoukro", "Parc de Taï", "Man", "Assinie"],
    bestTimeToVisit: {
      fr: "Novembre à Mars (grande saison sèche)",
      en: "November to March (main dry season)",
      es: "Noviembre a Marzo (temporada seca principal)"
    },
    relatedCountries: ["Ghana", "Liberia", "Mali", "Burkina Faso"]
  },
  "Égypte": {
    slug: "egypte",
    flag: "🇪🇬",
    description: {
      fr: "L'Égypte fascine par ses trésors antiques : pyramides de Gizeh, temples de Louxor, croisière sur le Nil et tombeaux de la Vallée des Rois. La terre des pharaons offre un voyage dans 5000 ans d'histoire.",
      en: "Egypt fascinates with ancient treasures: Giza pyramids, Luxor temples, Nile cruises, and Valley of the Kings tombs. The land of pharaohs offers a journey through 5000 years of history.",
      es: "Egipto fascina con sus tesoros antiguos: pirámides de Guiza, templos de Luxor, crucero por el Nilo y tumbas del Valle de los Reyes. La tierra de los faraones ofrece un viaje por 5000 años de historia."
    },
    extendedDescription: {
      fr: "Du Caire aux temples d'Abou Simbel, l'Égypte dévoile un patrimoine inégalé. Une croisière sur le Nil entre Louxor et Assouan traverse les sites les plus emblématiques. La mer Rouge offre plongée et farniente.",
      en: "From Cairo to Abu Simbel temples, Egypt unveils unmatched heritage. A Nile cruise between Luxor and Aswan crosses the most iconic sites. The Red Sea offers diving and relaxation.",
      es: "Del Cairo a los templos de Abu Simbel, Egipto revela un patrimonio inigualado."
    },
    keywords: {
      fr: ["voyage égypte", "pyramides", "croisière nil", "louxor"],
      en: ["egypt trip", "pyramids", "nile cruise", "luxor"],
      es: ["viaje egipto", "pirámides", "crucero nilo", "luxor"]
    },
    highlights: ["Le Caire", "Pyramides de Gizeh", "Louxor", "Assouan", "Abou Simbel", "Hurghada"],
    bestTimeToVisit: {
      fr: "Octobre à Avril (températures clémentes)",
      en: "October to April (mild temperatures)",
      es: "Octubre a Abril (temperaturas suaves)"
    },
    relatedCountries: ["Jordanie", "Israël", "Maroc", "Tunisie"]
  },
  "Roumanie": {
    slug: "roumanie",
    flag: "🇷🇴",
    description: {
      fr: "La Roumanie enchante par ses châteaux légendaires, ses villages saxons préservés, les Carpates sauvages et le delta du Danube. La terre de Dracula offre une Europe authentique et mystérieuse.",
      en: "Romania enchants with legendary castles, preserved Saxon villages, wild Carpathians, and the Danube Delta. Dracula's land offers an authentic and mysterious Europe.",
      es: "Rumania encanta con sus castillos legendarios, pueblos sajones preservados, los Cárpatos salvajes y el delta del Danubio. La tierra de Drácula ofrece una Europa auténtica y misteriosa."
    },
    extendedDescription: {
      fr: "Le château de Bran et les églises fortifiées de Transylvanie sont emblématiques. Les Carpates abritent ours et loups. Le delta du Danube, réserve de biosphère UNESCO, est un paradis ornithologique.",
      en: "Bran Castle and Transylvania's fortified churches are iconic. Carpathians shelter bears and wolves. The Danube Delta, a UNESCO biosphere reserve, is a birdwatching paradise.",
      es: "El castillo de Bran y las iglesias fortificadas de Transilvania son emblemáticos."
    },
    keywords: {
      fr: ["voyage roumanie", "transylvanie", "château bran", "delta danube"],
      en: ["romania trip", "transylvania", "bran castle", "danube delta"],
      es: ["viaje rumania", "transilvania", "castillo bran", "delta danubio"]
    },
    highlights: ["Bucarest", "Brașov", "Château de Bran", "Sibiu", "Delta du Danube", "Sighișoara"],
    bestTimeToVisit: {
      fr: "Mai à Septembre",
      en: "May to September",
      es: "Mayo a Septiembre"
    },
    relatedCountries: ["Bulgarie", "Hongrie", "Moldavie", "Ukraine"]
  },
  "Albanie": {
    slug: "albanie",
    flag: "🇦🇱",
    description: {
      fr: "L'Albanie surprend par ses plages vierges de la Riviera, ses sites antiques UNESCO, ses montagnes albanaises et son hospitalité légendaire. La dernière frontière d'Europe offre authenticité et découverte.",
      en: "Albania surprises with pristine Riviera beaches, UNESCO ancient sites, Albanian Alps, and legendary hospitality. Europe's last frontier offers authenticity and discovery.",
      es: "Albania sorprende con sus playas vírgenes de la Riviera, sitios antiguos UNESCO, los Alpes albaneses y su hospitalidad legendaria. La última frontera de Europa ofrece autenticidad y descubrimiento."
    },
    extendedDescription: {
      fr: "De la Riviera albanaise aux Alpes du nord, le pays révèle des trésors cachés. Berat et Gjirokastër sont classées UNESCO. Le lac d'Ohrid et le parc de Theth offrent une nature préservée.",
      en: "From the Albanian Riviera to northern Alps, the country reveals hidden treasures. Berat and Gjirokastër are UNESCO-listed. Lake Ohrid and Theth Park offer pristine nature.",
      es: "De la Riviera albanesa a los Alpes del norte, el país revela tesoros escondidos."
    },
    keywords: {
      fr: ["voyage albanie", "riviera albanaise", "berat", "theth"],
      en: ["albania trip", "albanian riviera", "berat", "theth"],
      es: ["viaje albania", "riviera albanesa", "berat", "theth"]
    },
    highlights: ["Tirana", "Berat", "Gjirokastër", "Riviera albanaise", "Theth", "Sarandë"],
    bestTimeToVisit: {
      fr: "Mai à Septembre",
      en: "May to September",
      es: "Mayo a Septiembre"
    },
    relatedCountries: ["Monténégro", "Grèce", "Macédoine du Nord", "Kosovo"]
  },
  "Arménie": {
    slug: "armenie",
    flag: "🇦🇲",
    description: {
      fr: "L'Arménie émeut par ses monastères millénaires perchés, ses paysages montagneux du Caucase et sa culture ancestrale. Premier pays chrétien au monde, elle offre un patrimoine spirituel unique.",
      en: "Armenia moves with millennia-old perched monasteries, Caucasus mountain landscapes, and ancestral culture. The world's first Christian country offers unique spiritual heritage.",
      es: "Armenia conmueve con sus monasterios milenarios encaramados, paisajes montañosos del Cáucaso y cultura ancestral. Primer país cristiano del mundo, ofrece un patrimonio espiritual único."
    },
    extendedDescription: {
      fr: "Le monastère de Geghard creusé dans la roche et Tatev accessible par téléphérique sont emblématiques. Le lac Sevan, les khatchkars sculptées et le mont Ararat en toile de fond composent des paysages uniques.",
      en: "Geghard Monastery carved into rock and Tatev accessible by cable car are iconic. Lake Sevan, carved khachkars, and Mount Ararat backdrop create unique landscapes.",
      es: "El monasterio de Geghard tallado en la roca y Tatev accesible por teleférico son emblemáticos."
    },
    keywords: {
      fr: ["voyage arménie", "monastère geghard", "lac sevan", "tatev"],
      en: ["armenia trip", "geghard monastery", "lake sevan", "tatev"],
      es: ["viaje armenia", "monasterio geghard", "lago sevan", "tatev"]
    },
    highlights: ["Erevan", "Monastère de Geghard", "Lac Sevan", "Tatev", "Garni", "Khor Virap"],
    bestTimeToVisit: {
      fr: "Mai à Octobre",
      en: "May to October",
      es: "Mayo a Octubre"
    },
    relatedCountries: ["Géorgie", "Iran", "Turquie", "Azerbaïdjan"]
  },
  "Salvador": {
    slug: "salvador",
    flag: "🇸🇻",
    description: {
      fr: "Le Salvador séduit par ses volcans spectaculaires, ses plages de surf légendaires, ses villages coloniaux colorés et sa culture maya vibrante. Le plus petit pays d'Amérique centrale offre une authenticité rare.",
      en: "El Salvador seduces with spectacular volcanoes, legendary surf beaches, colorful colonial villages, and vibrant Mayan culture. Central America's smallest country offers rare authenticity.",
      es: "El Salvador seduce con sus volcanes espectaculares, playas de surf legendarias, pueblos coloniales coloridos y cultura maya vibrante. El país más pequeño de Centroamérica ofrece una autenticidad rara."
    },
    extendedDescription: {
      fr: "La route des fleurs traverse villages artisanaux et plantations de café. El Tunco et La Libertad attirent les surfeurs du monde entier. Le site maya de Joya de Cerén est le Pompéi d'Amérique.",
      en: "The Flower Route crosses artisan villages and coffee plantations. El Tunco and La Libertad attract surfers worldwide. Joya de Cerén Mayan site is the Pompeii of America.",
      es: "La Ruta de las Flores atraviesa pueblos artesanales y plantaciones de café."
    },
    keywords: {
      fr: ["voyage salvador", "surf el tunco", "route des fleurs", "volcans salvador"],
      en: ["el salvador trip", "el tunco surf", "flower route", "salvador volcanoes"],
      es: ["viaje el salvador", "surf el tunco", "ruta de las flores", "volcanes el salvador"]
    },
    highlights: ["San Salvador", "El Tunco", "Route des Fleurs", "Suchitoto", "Santa Ana", "Joya de Cerén"],
    bestTimeToVisit: {
      fr: "Novembre à Avril (saison sèche)",
      en: "November to April (dry season)",
      es: "Noviembre a Abril (temporada seca)"
    },
    relatedCountries: ["Guatemala", "Honduras", "Nicaragua", "Costa Rica"]
  },
  "Honduras": {
    slug: "honduras",
    flag: "🇭🇳",
    description: {
      fr: "Le Honduras fascine par les ruines mayas de Copán, les récifs de Roatán, ses parcs nationaux préservés et ses villages garifunas. Un trésor d'Amérique centrale encore méconnu des voyageurs.",
      en: "Honduras fascinates with Copán Mayan ruins, Roatán reefs, preserved national parks, and Garifuna villages. A Central American treasure still unknown to travelers.",
      es: "Honduras fascina con las ruinas mayas de Copán, los arrecifes de Roatán, sus parques nacionales preservados y pueblos garífunas. Un tesoro de Centroamérica aún desconocido para los viajeros."
    },
    extendedDescription: {
      fr: "Copán est l'un des sites mayas les plus importants avec ses stèles sculptées. Roatán offre une plongée exceptionnelle dans la barrière de corail mésoaméricaine. La Mosquitia reste l'une des régions les plus sauvages d'Amérique.",
      en: "Copán is one of the most important Mayan sites with carved stelae. Roatán offers exceptional diving in the Mesoamerican barrier reef. Mosquitia remains one of America's wildest regions.",
      es: "Copán es uno de los sitios mayas más importantes con estelas talladas."
    },
    keywords: {
      fr: ["voyage honduras", "copan", "roatan plongée", "îles de la baie"],
      en: ["honduras trip", "copan ruins", "roatan diving", "bay islands"],
      es: ["viaje honduras", "copán", "buceo roatán", "islas de la bahía"]
    },
    highlights: ["Tegucigalpa", "Copán", "Roatán", "Utila", "La Ceiba", "Gracias"],
    bestTimeToVisit: {
      fr: "Février à Mai (saison sèche)",
      en: "February to May (dry season)",
      es: "Febrero a Mayo (temporada seca)"
    },
    relatedCountries: ["Guatemala", "Salvador", "Nicaragua", "Belize"]
  },
  "Paraguay": {
    slug: "paraguay",
    flag: "🇵🇾",
    description: {
      fr: "Le Paraguay surprend par ses missions jésuites UNESCO, le Chaco sauvage, ses traditions guaranies vivantes et son artisanat unique. Le cœur de l'Amérique du Sud offre une expérience authentique.",
      en: "Paraguay surprises with UNESCO Jesuit missions, wild Chaco, living Guaraní traditions, and unique crafts. The heart of South America offers an authentic experience.",
      es: "Paraguay sorprende con sus misiones jesuitas UNESCO, el Chaco salvaje, sus tradiciones guaraníes vivas y artesanía única. El corazón de Sudamérica ofrece una experiencia auténtica."
    },
    extendedDescription: {
      fr: "Les missions jésuites de Trinidad et Jesús témoignent d'un passé unique. Le Chaco abrite jaguars et peuples indigènes. Asunción mêle architecture coloniale et modernité le long du fleuve Paraguay.",
      en: "Trinidad and Jesús Jesuit missions testify to a unique past. The Chaco shelters jaguars and indigenous peoples. Asunción mixes colonial architecture and modernity along the Paraguay River.",
      es: "Las misiones jesuitas de Trinidad y Jesús dan testimonio de un pasado único."
    },
    keywords: {
      fr: ["voyage paraguay", "missions jésuites", "chaco", "guarani"],
      en: ["paraguay trip", "jesuit missions", "chaco", "guarani culture"],
      es: ["viaje paraguay", "misiones jesuitas", "chaco", "guaraní"]
    },
    highlights: ["Asunción", "Missions jésuites", "Chaco", "Encarnación", "San Bernardino", "Itaipú"],
    bestTimeToVisit: {
      fr: "Mai à Septembre (hiver austral, plus frais)",
      en: "May to September (austral winter, cooler)",
      es: "Mayo a Septiembre (invierno austral, más fresco)"
    },
    relatedCountries: ["Argentine", "Brésil", "Bolivie", "Uruguay"]
  },
  "Uruguay": {
    slug: "uruguay",
    flag: "🇺🇾",
    description: {
      fr: "L'Uruguay charme par ses plages de l'Atlantique, Montevideo cosmopolite, les estancias de la pampa et Colonia del Sacramento coloniale. Le petit pays entre géants offre douceur de vivre et authenticité.",
      en: "Uruguay charms with Atlantic beaches, cosmopolitan Montevideo, pampa estancias, and colonial Colonia del Sacramento. The small country between giants offers quality of life and authenticity.",
      es: "Uruguay encanta con sus playas del Atlántico, Montevideo cosmopolita, las estancias de la pampa y Colonia del Sacramento colonial. El pequeño país entre gigantes ofrece calidad de vida y autenticidad."
    },
    extendedDescription: {
      fr: "Punta del Este est la Saint-Tropez d'Amérique du Sud. Colonia del Sacramento, ville coloniale UNESCO, charme par ses rues pavées. Les estancias offrent une immersion dans la culture gaucho.",
      en: "Punta del Este is South America's Saint-Tropez. Colonia del Sacramento, UNESCO colonial town, charms with cobblestone streets. Estancias offer immersion in gaucho culture.",
      es: "Punta del Este es la Saint-Tropez de Sudamérica."
    },
    keywords: {
      fr: ["voyage uruguay", "punta del este", "colonia", "montevideo"],
      en: ["uruguay trip", "punta del este", "colonia", "montevideo"],
      es: ["viaje uruguay", "punta del este", "colonia", "montevideo"]
    },
    highlights: ["Montevideo", "Punta del Este", "Colonia del Sacramento", "Cabo Polonio", "Rocha", "Carmelo"],
    bestTimeToVisit: {
      fr: "Décembre à Mars (été austral)",
      en: "December to March (austral summer)",
      es: "Diciembre a Marzo (verano austral)"
    },
    relatedCountries: ["Argentine", "Brésil", "Paraguay", "Chili"]
  },
  "Andorre": {
    slug: "andorre",
    flag: "🇦🇩",
    description: {
      fr: "L'Andorre séduit par ses pistes de ski pyrénéennes, son shopping détaxé, ses vallées préservées et son patrimoine roman. La principauté entre France et Espagne offre montagne et détente.",
      en: "Andorra seduces with Pyrenean ski slopes, duty-free shopping, preserved valleys, and Romanesque heritage. The principality between France and Spain offers mountains and relaxation.",
      es: "Andorra seduce con sus pistas de esquí pirenaicas, compras libres de impuestos, valles preservados y patrimonio románico. El principado entre Francia y España ofrece montaña y descanso."
    },
    extendedDescription: {
      fr: "Grandvalira et Vallnord offrent les plus grands domaines skiables des Pyrénées. Caldea est le plus grand spa thermal d'Europe. Les églises romanes de la vallée de Sant Joan sont remarquables.",
      en: "Grandvalira and Vallnord offer the largest ski areas in the Pyrenees. Caldea is Europe's largest thermal spa. Sant Joan Valley Romanesque churches are remarkable.",
      es: "Grandvalira y Vallnord ofrecen las mayores estaciones de esquí de los Pirineos."
    },
    keywords: {
      fr: ["voyage andorre", "ski andorre", "caldea", "shopping andorre"],
      en: ["andorra trip", "andorra skiing", "caldea spa", "andorra shopping"],
      es: ["viaje andorra", "esquí andorra", "caldea", "compras andorra"]
    },
    highlights: ["Andorre-la-Vieille", "Grandvalira", "Caldea", "Vallnord", "Ordino", "Canillo"],
    bestTimeToVisit: {
      fr: "Décembre à Mars (ski) ou Juin à Septembre (randonnée)",
      en: "December to March (skiing) or June to September (hiking)",
      es: "Diciembre a Marzo (esquí) o Junio a Septiembre (senderismo)"
    },
    relatedCountries: ["France", "Espagne", "Monaco", "Suisse"]
  },
  "Liban": {
    slug: "liban",
    flag: "🇱🇧",
    description: {
      fr: "Le Liban fascine par ses ruines de Baalbek, ses cèdres millénaires, Beyrouth cosmopolite et sa gastronomie légendaire. Le pays des Phéniciens offre histoire et saveurs méditerranéennes.",
      en: "Lebanon fascinates with Baalbek ruins, millennial cedars, cosmopolitan Beirut, and legendary cuisine. The land of Phoenicians offers history and Mediterranean flavors.",
      es: "Líbano fascina con las ruinas de Baalbek, sus cedros milenarios, Beirut cosmopolita y su gastronomía legendaria. El país de los fenicios ofrece historia y sabores mediterráneos."
    },
    extendedDescription: {
      fr: "Baalbek abrite les temples romains les mieux conservés. Byblos est l'une des plus anciennes villes habitées. La vallée de la Bekaa produit des vins renommés. Les mezze libanais sont incontournables.",
      en: "Baalbek houses the best-preserved Roman temples. Byblos is one of the oldest inhabited cities. The Bekaa Valley produces renowned wines. Lebanese mezze are unmissable.",
      es: "Baalbek alberga los templos romanos mejor conservados."
    },
    keywords: {
      fr: ["voyage liban", "baalbek", "byblos", "beyrouth"],
      en: ["lebanon trip", "baalbek", "byblos", "beirut"],
      es: ["viaje líbano", "baalbek", "biblos", "beirut"]
    },
    highlights: ["Beyrouth", "Baalbek", "Byblos", "Tripoli", "Cèdres du Liban", "Grotte de Jeita"],
    bestTimeToVisit: {
      fr: "Avril à Juin et Septembre à Novembre",
      en: "April to June and September to November",
      es: "Abril a Junio y Septiembre a Noviembre"
    },
    relatedCountries: ["Jordanie", "Syrie", "Israël", "Chypre"]
  },
  "Venezuela": {
    slug: "venezuela",
    flag: "🇻🇪",
    description: {
      fr: "Le Venezuela émerveille par le Salto Ángel, la plus haute chute d'eau au monde, les plages caribéennes, les tepuis mystérieux et le delta de l'Orénoque. Une nature spectaculaire à découvrir.",
      en: "Venezuela amazes with Angel Falls, the world's highest waterfall, Caribbean beaches, mysterious tepuis, and the Orinoco Delta. Spectacular nature to discover.",
      es: "Venezuela maravilla con el Salto Ángel, la cascada más alta del mundo, playas caribeñas, tepuyes misteriosos y el delta del Orinoco. Una naturaleza espectacular por descubrir."
    },
    extendedDescription: {
      fr: "Le Salto Ángel plonge de 979 mètres dans la jungle. Le parc national de Canaima et ses tepuis offrent des paysages uniques. L'archipel de Los Roques est un paradis de plongée caribéen.",
      en: "Angel Falls plunges 979 meters into the jungle. Canaima National Park and its tepuis offer unique landscapes. Los Roques archipelago is a Caribbean diving paradise.",
      es: "El Salto Ángel cae 979 metros en la selva."
    },
    keywords: {
      fr: ["voyage venezuela", "salto angel", "canaima", "los roques"],
      en: ["venezuela trip", "angel falls", "canaima", "los roques"],
      es: ["viaje venezuela", "salto ángel", "canaima", "los roques"]
    },
    highlights: ["Caracas", "Salto Ángel", "Parc Canaima", "Los Roques", "Isla Margarita", "Delta de l'Orénoque"],
    bestTimeToVisit: {
      fr: "Décembre à Avril (saison sèche)",
      en: "December to April (dry season)",
      es: "Diciembre a Abril (temporada seca)"
    },
    relatedCountries: ["Colombie", "Brésil", "Guyana", "Trinidad-et-Tobago"]
  },
  "Belize": {
    slug: "belize",
    flag: "🇧🇿",
    description: {
      fr: "Le Belize enchante par sa barrière de corail UNESCO, le Grand Trou Bleu légendaire, ses ruines mayas enfouies dans la jungle et ses cayes paradisiaques. Un joyau caribéen d'Amérique centrale.",
      en: "Belize enchants with its UNESCO barrier reef, legendary Great Blue Hole, Mayan ruins buried in jungle, and paradise cayes. A Caribbean jewel of Central America.",
      es: "Belice encanta con su barrera de coral UNESCO, el legendario Gran Agujero Azul, ruinas mayas enterradas en la selva y cayos paradisíacos. Una joya caribeña de Centroamérica."
    },
    extendedDescription: {
      fr: "La deuxième plus grande barrière de corail au monde offre une plongée exceptionnelle. Les sites mayas de Caracol et Xunantunich rivalisent avec le Guatemala. Caye Caulker incarne le 'Go Slow' bélizien.",
      en: "The world's second-largest barrier reef offers exceptional diving. Caracol and Xunantunich Mayan sites rival Guatemala. Caye Caulker embodies the Belizean 'Go Slow' lifestyle.",
      es: "La segunda barrera de coral más grande del mundo ofrece buceo excepcional."
    },
    keywords: {
      fr: ["voyage belize", "grand trou bleu", "plongée belize", "caye caulker"],
      en: ["belize trip", "great blue hole", "belize diving", "caye caulker"],
      es: ["viaje belice", "gran agujero azul", "buceo belice", "caye caulker"]
    },
    highlights: ["Belize City", "Grand Trou Bleu", "Caye Caulker", "Ambergris Caye", "Caracol", "San Ignacio"],
    bestTimeToVisit: {
      fr: "Février à Mai (saison sèche)",
      en: "February to May (dry season)",
      es: "Febrero a Mayo (temporada seca)"
    },
    relatedCountries: ["Guatemala", "Mexique", "Honduras", "Costa Rica"]
  },
  "Nicaragua": {
    slug: "nicaragua",
    flag: "🇳🇮",
    description: {
      fr: "Le Nicaragua séduit par ses volcans fumants, le lac Nicaragua, les villes coloniales de Granada et León, et les plages sauvages du Pacifique. L'Amérique centrale authentique et abordable.",
      en: "Nicaragua seduces with smoking volcanoes, Lake Nicaragua, colonial cities Granada and León, and wild Pacific beaches. Authentic and affordable Central America.",
      es: "Nicaragua seduce con sus volcanes humeantes, el lago Nicaragua, las ciudades coloniales de Granada y León, y las playas salvajes del Pacífico. Centroamérica auténtica y asequible."
    },
    extendedDescription: {
      fr: "Granada charme par son architecture coloniale colorée. Le volcan Masaya offre l'un des rares lacs de lave accessibles. San Juan del Sur est le repaire des surfeurs. L'île d'Ometepe abrite deux volcans jumeaux.",
      en: "Granada charms with colorful colonial architecture. Masaya volcano offers one of the rare accessible lava lakes. San Juan del Sur is surfers' haven. Ometepe island hosts twin volcanoes.",
      es: "Granada encanta con su arquitectura colonial colorida."
    },
    keywords: {
      fr: ["voyage nicaragua", "granada", "volcan masaya", "ometepe"],
      en: ["nicaragua trip", "granada", "masaya volcano", "ometepe island"],
      es: ["viaje nicaragua", "granada", "volcán masaya", "ometepe"]
    },
    highlights: ["Managua", "Granada", "León", "Volcan Masaya", "Île d'Ometepe", "San Juan del Sur"],
    bestTimeToVisit: {
      fr: "Décembre à Avril (saison sèche)",
      en: "December to April (dry season)",
      es: "Diciembre a Abril (temporada seca)"
    },
    relatedCountries: ["Costa Rica", "Honduras", "Salvador", "Panama"]
  },
  "Porto Rico": {
    slug: "porto-rico",
    flag: "🇵🇷",
    description: {
      fr: "Porto Rico enchante par le vieux San Juan coloré, la forêt tropicale d'El Yunque, les baies bioluminescentes et ses plages caribéennes. L'île offre saveurs latino et facilités américaines.",
      en: "Puerto Rico enchants with colorful Old San Juan, El Yunque rainforest, bioluminescent bays, and Caribbean beaches. The island offers Latin flavors and American conveniences.",
      es: "Puerto Rico encanta con el viejo San Juan colorido, la selva tropical El Yunque, bahías bioluminiscentes y playas caribeñas. La isla ofrece sabores latinos y comodidades americanas."
    },
    extendedDescription: {
      fr: "Le vieux San Juan, fondé en 1521, est un joyau colonial. El Yunque est la seule forêt tropicale nationale américaine. Les baies de Mosquito et La Parguera brillent la nuit de plancton lumineux.",
      en: "Old San Juan, founded in 1521, is a colonial gem. El Yunque is the only American national tropical rainforest. Mosquito and La Parguera bays glow at night with luminous plankton.",
      es: "El viejo San Juan, fundado en 1521, es una joya colonial."
    },
    keywords: {
      fr: ["voyage porto rico", "san juan", "el yunque", "vieques"],
      en: ["puerto rico trip", "san juan", "el yunque", "vieques"],
      es: ["viaje puerto rico", "san juan", "el yunque", "vieques"]
    },
    highlights: ["San Juan", "El Yunque", "Vieques", "Culebra", "Ponce", "Baie bioluminescente"],
    bestTimeToVisit: {
      fr: "Décembre à Avril (saison sèche)",
      en: "December to April (dry season)",
      es: "Diciembre a Abril (temporada seca)"
    },
    relatedCountries: ["République dominicaine", "Cuba", "Îles Vierges", "Jamaïque"]
  },
  "Suriname": {
    slug: "suriname",
    flag: "🇸🇷",
    description: {
      fr: "Le Suriname surprend par sa forêt amazonienne intacte, Paramaribo multiculturelle UNESCO, ses communautés marronnes et amérindiennes. Le secret le mieux gardé d'Amérique du Sud.",
      en: "Suriname surprises with intact Amazon rainforest, multicultural UNESCO Paramaribo, Maroon and Amerindian communities. South America's best-kept secret.",
      es: "Surinam sorprende con su selva amazónica intacta, Paramaribo multicultural UNESCO, comunidades cimarronas y amerindias. El secreto mejor guardado de Sudamérica."
    },
    extendedDescription: {
      fr: "Paramaribo, patrimoine UNESCO, mélange architecture coloniale néerlandaise et mosquées, synagogues et temples hindous. La réserve naturelle centrale protège 93% du territoire. Les villages marrons préservent la culture africaine.",
      en: "Paramaribo, UNESCO heritage, blends Dutch colonial architecture with mosques, synagogues, and Hindu temples. The central nature reserve protects 93% of the territory. Maroon villages preserve African culture.",
      es: "Paramaribo, patrimonio UNESCO, mezcla arquitectura colonial holandesa con mezquitas, sinagogas y templos hindúes."
    },
    keywords: {
      fr: ["voyage suriname", "paramaribo", "amazonie suriname", "marrons"],
      en: ["suriname trip", "paramaribo", "suriname amazon", "maroon culture"],
      es: ["viaje surinam", "paramaribo", "amazonia surinam", "cimarrones"]
    },
    highlights: ["Paramaribo", "Réserve naturelle centrale", "Brownsberg", "Galibi", "Upper Suriname", "Jodensavanne"],
    bestTimeToVisit: {
      fr: "Février à Avril et Août à Novembre (saisons sèches)",
      en: "February to April and August to November (dry seasons)",
      es: "Febrero a Abril y Agosto a Noviembre (temporadas secas)"
    },
    relatedCountries: ["Guyana", "Guyane française", "Brésil", "Venezuela"]
  },
  "Qatar": {
    slug: "qatar",
    flag: "🇶🇦",
    description: {
      fr: "Le Qatar impressionne par Doha futuriste, ses musées d'art exceptionnels, le désert et la mer intérieure, et son patrimoine bédouin. L'émirat du Golfe marie tradition et modernité architecturale.",
      en: "Qatar impresses with futuristic Doha, exceptional art museums, desert and inland sea, and Bedouin heritage. The Gulf emirate marries tradition and architectural modernity.",
      es: "Qatar impresiona con Doha futurista, sus museos de arte excepcionales, el desierto y el mar interior, y su patrimonio beduino. El emirato del Golfo combina tradición y modernidad arquitectónica."
    },
    extendedDescription: {
      fr: "Le Musée d'Art Islamique de I.M. Pei est un chef-d'œuvre. La mer intérieure de Khor Al Adaid offre des dunes spectaculaires. Souq Waqif préserve l'atmosphère traditionnelle au cœur de Doha moderne.",
      en: "I.M. Pei's Museum of Islamic Art is a masterpiece. Khor Al Adaid inland sea offers spectacular dunes. Souq Waqif preserves traditional atmosphere in modern Doha's heart.",
      es: "El Museo de Arte Islámico de I.M. Pei es una obra maestra."
    },
    keywords: {
      fr: ["voyage qatar", "doha", "musée art islamique", "desert qatar"],
      en: ["qatar trip", "doha", "islamic art museum", "qatar desert"],
      es: ["viaje qatar", "doha", "museo arte islámico", "desierto qatar"]
    },
    highlights: ["Doha", "Musée d'Art Islamique", "Souq Waqif", "The Pearl", "Khor Al Adaid", "Katara"],
    bestTimeToVisit: {
      fr: "Novembre à Mars (températures clémentes)",
      en: "November to March (mild temperatures)",
      es: "Noviembre a Marzo (temperaturas suaves)"
    },
    relatedCountries: ["Émirats arabes unis", "Bahreïn", "Arabie Saoudite", "Oman"]
  },
  "Oman": {
    slug: "oman",
    flag: "🇴🇲",
    description: {
      fr: "Oman enchante par ses wadis turquoise, le désert de Wahiba, les forts historiques et la modernité de Mascate. Le sultanat préserve traditions et nature spectaculaire dans un Golfe en mutation.",
      en: "Oman enchants with turquoise wadis, Wahiba desert, historic forts, and Muscat's modernity. The sultanate preserves traditions and spectacular nature in a changing Gulf.",
      es: "Omán encanta con sus wadis turquesas, el desierto de Wahiba, fuertes históricos y la modernidad de Mascate. El sultanato preserva tradiciones y naturaleza espectacular en un Golfo cambiante."
    },
    extendedDescription: {
      fr: "Mascate marie Grande Mosquée du Sultan Qaboos et souk de Muttrah. Les wadis Shab et Bani Khalid offrent des oasis de fraîcheur. Nizwa et son fort témoignent du patrimoine omanais. Salalah verdit pendant la mousson.",
      en: "Muscat marries Sultan Qaboos Grand Mosque and Muttrah souk. Wadis Shab and Bani Khalid offer oases of freshness. Nizwa and its fort testify to Omani heritage. Salalah turns green during monsoon.",
      es: "Mascate combina la Gran Mezquita del Sultán Qaboos y el zoco de Muttrah."
    },
    keywords: {
      fr: ["voyage oman", "mascate", "wadi shab", "désert wahiba"],
      en: ["oman trip", "muscat", "wadi shab", "wahiba sands"],
      es: ["viaje omán", "mascate", "wadi shab", "desierto wahiba"]
    },
    highlights: ["Mascate", "Nizwa", "Wadi Shab", "Désert de Wahiba", "Jebel Shams", "Salalah"],
    bestTimeToVisit: {
      fr: "Octobre à Avril (hiver, plus frais)",
      en: "October to April (winter, cooler)",
      es: "Octubre a Abril (invierno, más fresco)"
    },
    relatedCountries: ["Émirats arabes unis", "Yémen", "Arabie Saoudite", "Qatar"]
  },
  "Slovaquie": {
    slug: "slovaquie",
    flag: "🇸🇰",
    description: {
      fr: "La Slovaquie séduit par les Hautes Tatras majestueuses, ses châteaux perchés, la vieille ville de Bratislava et ses villages folkloriques. Le cœur de l'Europe centrale offre nature et patrimoine préservés.",
      en: "Slovakia seduces with majestic High Tatras, perched castles, Bratislava's old town, and folk villages. The heart of Central Europe offers preserved nature and heritage.",
      es: "Eslovaquia seduce con los majestuosos Altos Tatras, castillos encaramados, el casco antiguo de Bratislava y pueblos folclóricos. El corazón de Europa central ofrece naturaleza y patrimonio preservados."
    },
    extendedDescription: {
      fr: "Les Hautes Tatras, les plus petites hautes montagnes au monde, offrent randonnées et ski. Le château de Spiš est l'un des plus grands d'Europe. Bardejov et Vlkolínec préservent l'architecture traditionnelle.",
      en: "High Tatras, the world's smallest high mountains, offer hiking and skiing. Spiš Castle is one of Europe's largest. Bardejov and Vlkolínec preserve traditional architecture.",
      es: "Los Altos Tatras, las montañas altas más pequeñas del mundo, ofrecen senderismo y esquí."
    },
    keywords: {
      fr: ["voyage slovaquie", "hautes tatras", "bratislava", "château spiš"],
      en: ["slovakia trip", "high tatras", "bratislava", "spis castle"],
      es: ["viaje eslovaquia", "altos tatras", "bratislava", "castillo spiš"]
    },
    highlights: ["Bratislava", "Hautes Tatras", "Château de Spiš", "Košice", "Banská Štiavnica", "Bardejov"],
    bestTimeToVisit: {
      fr: "Mai à Septembre (été) ou Décembre à Mars (ski)",
      en: "May to September (summer) or December to March (skiing)",
      es: "Mayo a Septiembre (verano) o Diciembre a Marzo (esquí)"
    },
    relatedCountries: ["République tchèque", "Pologne", "Hongrie", "Autriche"]
  },
  "Allemagne": {
    slug: "allemagne",
    flag: "🇩🇪",
    description: {
      fr: "L'Allemagne fascine par ses châteaux bavarois, Berlin vibrante, la forêt Noire romantique et ses marchés de Noël légendaires. Le pays marie histoire, culture et efficacité germanique.",
      en: "Germany fascinates with Bavarian castles, vibrant Berlin, romantic Black Forest, and legendary Christmas markets. The country marries history, culture, and German efficiency.",
      es: "Alemania fascina con sus castillos bávaros, Berlín vibrante, la romántica Selva Negra y sus legendarios mercados navideños. El país combina historia, cultura y eficiencia germánica."
    },
    extendedDescription: {
      fr: "Berlin raconte l'histoire du XXe siècle. Le château de Neuschwanstein a inspiré Disney. Munich combine tradition bavaroise et modernité. La route romantique traverse villages médiévaux et vignobles.",
      en: "Berlin tells 20th-century history. Neuschwanstein Castle inspired Disney. Munich combines Bavarian tradition and modernity. The Romantic Road crosses medieval villages and vineyards.",
      es: "Berlín cuenta la historia del siglo XX."
    },
    keywords: {
      fr: ["voyage allemagne", "berlin", "munich", "neuschwanstein"],
      en: ["germany trip", "berlin", "munich", "neuschwanstein castle"],
      es: ["viaje alemania", "berlín", "múnich", "neuschwanstein"]
    },
    highlights: ["Berlin", "Munich", "Neuschwanstein", "Hambourg", "Cologne", "Forêt-Noire"],
    bestTimeToVisit: {
      fr: "Mai à Septembre ou Décembre (marchés de Noël)",
      en: "May to September or December (Christmas markets)",
      es: "Mayo a Septiembre o Diciembre (mercados navideños)"
    },
    relatedCountries: ["Autriche", "Suisse", "France", "Pays-Bas"]
  },
  "Serbie": {
    slug: "serbie",
    flag: "🇷🇸",
    description: {
      fr: "La Serbie surprend par Belgrade festive, ses monastères orthodoxes UNESCO, le parc national de Tara et son hospitalité légendaire. Le carrefour des Balkans offre authenticité et vie nocturne.",
      en: "Serbia surprises with festive Belgrade, UNESCO Orthodox monasteries, Tara National Park, and legendary hospitality. The Balkans crossroads offers authenticity and nightlife.",
      es: "Serbia sorprende con Belgrado festiva, sus monasterios ortodoxos UNESCO, el Parque Nacional de Tara y su hospitalidad legendaria. El cruce de los Balcanes ofrece autenticidad y vida nocturna."
    },
    extendedDescription: {
      fr: "Belgrade est réputée pour sa vie nocturne sur péniches (splavovi). Les monastères de Studenica et Sopoćani sont classés UNESCO. Le parc de Đerdap longe les gorges spectaculaires du Danube.",
      en: "Belgrade is famous for its houseboat nightlife (splavovi). Studenica and Sopoćani monasteries are UNESCO-listed. Đerdap Park runs along the Danube's spectacular gorges.",
      es: "Belgrado es famosa por su vida nocturna en barcazas (splavovi)."
    },
    keywords: {
      fr: ["voyage serbie", "belgrade", "monastères serbie", "danube serbie"],
      en: ["serbia trip", "belgrade", "serbian monasteries", "danube serbia"],
      es: ["viaje serbia", "belgrado", "monasterios serbia", "danubio serbia"]
    },
    highlights: ["Belgrade", "Novi Sad", "Monastère de Studenica", "Parc de Tara", "Niš", "Đerdap"],
    bestTimeToVisit: {
      fr: "Mai à Septembre",
      en: "May to September",
      es: "Mayo a Septiembre"
    },
    relatedCountries: ["Croatie", "Bosnie-Herzégovine", "Monténégro", "Hongrie"]
  },
  "Népal": {
    slug: "nepal",
    flag: "🇳🇵",
    description: {
      fr: "Le Népal émerveille par l'Himalaya et l'Everest, les temples de Katmandou, les treks légendaires et sa spiritualité. Le toit du monde offre aventure et rencontres inoubliables.",
      en: "Nepal amazes with the Himalayas and Everest, Kathmandu temples, legendary treks, and spirituality. The roof of the world offers adventure and unforgettable encounters.",
      es: "Nepal maravilla con el Himalaya y el Everest, los templos de Katmandú, treks legendarios y espiritualidad. El techo del mundo ofrece aventura y encuentros inolvidables."
    },
    extendedDescription: {
      fr: "Le camp de base de l'Everest et le circuit des Annapurnas sont des treks mythiques. Katmandou et Patan regorgent de temples hindous et bouddhistes. Pokhara offre détente face aux sommets enneigés.",
      en: "Everest Base Camp and Annapurna Circuit are legendary treks. Kathmandu and Patan overflow with Hindu and Buddhist temples. Pokhara offers relaxation facing snow-capped peaks.",
      es: "El campo base del Everest y el circuito de los Annapurnas son treks míticos."
    },
    keywords: {
      fr: ["voyage népal", "everest", "trek annapurna", "katmandou"],
      en: ["nepal trip", "everest", "annapurna trek", "kathmandu"],
      es: ["viaje nepal", "everest", "trek annapurna", "katmandú"]
    },
    highlights: ["Katmandou", "Pokhara", "Everest", "Annapurnas", "Chitwan", "Lumbini"],
    bestTimeToVisit: {
      fr: "Octobre à Novembre et Mars à Avril (saisons de trek)",
      en: "October to November and March to April (trekking seasons)",
      es: "Octubre a Noviembre y Marzo a Abril (temporadas de trek)"
    },
    relatedCountries: ["Inde", "Tibet", "Bhoutan", "Sri Lanka"]
  },
  "Tadjikistan": {
    slug: "tadjikistan",
    flag: "🇹🇯",
    description: {
      fr: "Le Tadjikistan impressionne par les montagnes du Pamir, la route du Wakhan, les lacs d'altitude et la culture sogdienne. L'Asie centrale secrète offre des paysages parmi les plus spectaculaires au monde.",
      en: "Tajikistan impresses with Pamir Mountains, the Wakhan route, high-altitude lakes, and Sogdian culture. Secret Central Asia offers some of the world's most spectacular landscapes.",
      es: "Tayikistán impresiona con las montañas del Pamir, la ruta de Wakhan, lagos de altura y cultura sogdiana. Asia Central secreta ofrece algunos de los paisajes más espectaculares del mundo."
    },
    extendedDescription: {
      fr: "La Pamir Highway est l'une des routes les plus spectaculaires au monde. Le lac Iskanderkul brille d'un bleu profond. Douchanbé mêle héritage soviétique et renouveau tadjik. Khorog est la porte du Pamir.",
      en: "The Pamir Highway is one of the world's most spectacular roads. Iskanderkul Lake shines deep blue. Dushanbe mixes Soviet heritage and Tajik renewal. Khorog is the gateway to Pamir.",
      es: "La carretera del Pamir es una de las más espectaculares del mundo."
    },
    keywords: {
      fr: ["voyage tadjikistan", "pamir highway", "lac iskanderkul", "wakhan"],
      en: ["tajikistan trip", "pamir highway", "iskanderkul lake", "wakhan corridor"],
      es: ["viaje tayikistán", "carretera pamir", "lago iskanderkul", "wakhan"]
    },
    highlights: ["Douchanbé", "Pamir Highway", "Lac Iskanderkul", "Khorog", "Vallée de Wakhan", "Pendjikent"],
    bestTimeToVisit: {
      fr: "Juin à Septembre (routes du Pamir praticables)",
      en: "June to September (Pamir roads passable)",
      es: "Junio a Septiembre (carreteras del Pamir transitables)"
    },
    relatedCountries: ["Kirghizistan", "Ouzbékistan", "Afghanistan", "Chine"]
  },
  "Birmanie": {
    slug: "birmanie",
    flag: "🇲🇲",
    description: {
      fr: "La Birmanie (Myanmar) fascine par les milliers de temples de Bagan, le lac Inle et ses pêcheurs, la pagode Shwedagon dorée et ses ethnies préservées. Une Asie authentique et spirituelle.",
      en: "Myanmar (Burma) fascinates with thousands of Bagan temples, Inle Lake and its fishermen, golden Shwedagon Pagoda, and preserved ethnic groups. Authentic and spiritual Asia.",
      es: "Birmania (Myanmar) fascina con los miles de templos de Bagan, el lago Inle y sus pescadores, la pagoda dorada Shwedagon y sus etnias preservadas. Un Asia auténtica y espiritual."
    },
    extendedDescription: {
      fr: "Bagan compte plus de 2000 temples sur ses plaines. Le lac Inle offre des scènes de vie unique avec ses pêcheurs ramant avec la jambe. Yangon préserve un charme colonial britannique. Mandalay est le cœur spirituel.",
      en: "Bagan has over 2000 temples on its plains. Inle Lake offers unique life scenes with leg-rowing fishermen. Yangon preserves British colonial charm. Mandalay is the spiritual heart.",
      es: "Bagan cuenta con más de 2000 templos en sus llanuras."
    },
    keywords: {
      fr: ["voyage birmanie", "bagan", "lac inle", "yangon"],
      en: ["myanmar trip", "bagan temples", "inle lake", "yangon"],
      es: ["viaje birmania", "bagan", "lago inle", "rangún"]
    },
    highlights: ["Yangon", "Bagan", "Lac Inle", "Mandalay", "Ngapali Beach", "Rocher d'Or"],
    bestTimeToVisit: {
      fr: "Novembre à Février (saison sèche et fraîche)",
      en: "November to February (dry and cool season)",
      es: "Noviembre a Febrero (temporada seca y fresca)"
    },
    relatedCountries: ["Thaïlande", "Laos", "Inde", "Chine"]
  },
  "Laos": {
    slug: "laos",
    flag: "🇱🇦",
    description: {
      fr: "Le Laos enchante par Luang Prabang et ses moines à l'aube, le Mékong paisible, les temples dorés et son rythme de vie détendu. Le pays du million d'éléphants préserve une Asie authentique.",
      en: "Laos enchants with Luang Prabang and its dawn monks, peaceful Mekong, golden temples, and relaxed pace of life. The land of a million elephants preserves authentic Asia.",
      es: "Laos encanta con Luang Prabang y sus monjes al amanecer, el Mekong tranquilo, templos dorados y su ritmo de vida relajado. El país del millón de elefantes preserva un Asia auténtica."
    },
    extendedDescription: {
      fr: "Luang Prabang, patrimoine UNESCO, offre l'aumône des moines au lever du soleil. Les 4000 îles du sud invitent à la détente. Vang Vieng alterne grottes karstiques et activités outdoor. Vientiane reste une capitale paisible.",
      en: "Luang Prabang, UNESCO heritage, offers the monks' alms ceremony at sunrise. The 4000 southern islands invite relaxation. Vang Vieng alternates karst caves and outdoor activities. Vientiane remains a peaceful capital.",
      es: "Luang Prabang, patrimonio UNESCO, ofrece la limosna de los monjes al amanecer."
    },
    keywords: {
      fr: ["voyage laos", "luang prabang", "mékong", "4000 îles"],
      en: ["laos trip", "luang prabang", "mekong", "4000 islands"],
      es: ["viaje laos", "luang prabang", "mekong", "4000 islas"]
    },
    highlights: ["Luang Prabang", "Vientiane", "Vang Vieng", "4000 Îles", "Plaine des Jarres", "Pak Ou"],
    bestTimeToVisit: {
      fr: "Novembre à Février (saison sèche et fraîche)",
      en: "November to February (dry and cool season)",
      es: "Noviembre a Febrero (temporada seca y fresca)"
    },
    relatedCountries: ["Vietnam", "Cambodge", "Thaïlande", "Birmanie"]
  },
  "Mongolie": {
    slug: "mongolie",
    flag: "🇲🇳",
    description: {
      fr: "La Mongolie émerveille par ses steppes infinies, le désert de Gobi, les traditions nomades vivantes et l'hospitalité des yourtes. Le pays de Gengis Khan offre une immersion unique dans la vie nomade.",
      en: "Mongolia amazes with endless steppes, Gobi Desert, living nomadic traditions, and yurt hospitality. Genghis Khan's land offers a unique immersion in nomadic life.",
      es: "Mongolia maravilla con sus estepas infinitas, el desierto de Gobi, tradiciones nómadas vivas y la hospitalidad de las yurtas. La tierra de Gengis Kan ofrece una inmersión única en la vida nómada."
    },
    extendedDescription: {
      fr: "Oulan-Bator mêle histoire mongole et ère soviétique. Le désert de Gobi révèle dunes, fossiles de dinosaures et nomades. Le lac Khövsgöl est la perle bleue de Mongolie. Le festival du Naadam célèbre lutte, tir à l'arc et courses de chevaux.",
      en: "Ulaanbaatar mixes Mongolian history and Soviet era. Gobi Desert reveals dunes, dinosaur fossils, and nomads. Khövsgöl Lake is Mongolia's blue pearl. Naadam festival celebrates wrestling, archery, and horse racing.",
      es: "Ulán Bator mezcla historia mongola y era soviética."
    },
    keywords: {
      fr: ["voyage mongolie", "gobi", "yourte mongole", "naadam"],
      en: ["mongolia trip", "gobi desert", "mongolian yurt", "naadam festival"],
      es: ["viaje mongolia", "gobi", "yurta mongola", "naadam"]
    },
    highlights: ["Oulan-Bator", "Désert de Gobi", "Lac Khövsgöl", "Vallée de l'Orkhon", "Karakorum", "Parc de Terelj"],
    bestTimeToVisit: {
      fr: "Juin à Septembre (été, festival Naadam en juillet)",
      en: "June to September (summer, Naadam festival in July)",
      es: "Junio a Septiembre (verano, festival Naadam en julio)"
    },
    relatedCountries: ["Russie", "Chine", "Kazakhstan", "Kirghizistan"]
  },
  "Corée du Sud": {
    slug: "coree-du-sud",
    flag: "🇰🇷",
    description: {
      fr: "La Corée du Sud fascine par Séoul hypermoderne, les temples anciens, la K-pop et la gastronomie. Le pays du Matin calme marie tradition confucéenne et innovation technologique.",
      en: "South Korea fascinates with hypermodern Seoul, ancient temples, K-pop, and gastronomy. The Land of the Morning Calm marries Confucian tradition and technological innovation.",
      es: "Corea del Sur fascina con Seúl hipermoderna, templos antiguos, K-pop y gastronomía. El país de la Calma Matutina combina tradición confuciana e innovación tecnológica."
    },
    extendedDescription: {
      fr: "Séoul vibre entre palais royaux et quartiers branchés de Gangnam et Hongdae. Gyeongju est le musée à ciel ouvert de la dynastie Silla. Busan offre plages et marchés de fruits de mer. L'île de Jeju est un paradis volcanique.",
      en: "Seoul vibrates between royal palaces and trendy Gangnam and Hongdae districts. Gyeongju is the open-air museum of the Silla dynasty. Busan offers beaches and seafood markets. Jeju Island is a volcanic paradise.",
      es: "Seúl vibra entre palacios reales y barrios de moda de Gangnam y Hongdae."
    },
    keywords: {
      fr: ["voyage corée du sud", "séoul", "jeju", "temple coréen"],
      en: ["south korea trip", "seoul", "jeju island", "korean temple"],
      es: ["viaje corea del sur", "seúl", "jeju", "templo coreano"]
    },
    highlights: ["Séoul", "Busan", "Île de Jeju", "Gyeongju", "DMZ", "Jeonju"],
    bestTimeToVisit: {
      fr: "Avril à Mai (cerisiers) et Septembre à Novembre (automne)",
      en: "April to May (cherry blossoms) and September to November (autumn)",
      es: "Abril a Mayo (cerezos) y Septiembre a Noviembre (otoño)"
    },
    relatedCountries: ["Japon", "Chine", "Taïwan", "Vietnam"]
  }
};
